import axios from 'axios'

export interface TenantSeo {
  businessName: string
  businessCode: string
  ogImage: string
  favicon: string
  siteOrigin: string
  hideFromGoogle: boolean
}

// Only the tenant branding is cached; siteOrigin is rebuilt per request (see below).
type CachedTenantSeo = Omit<TenantSeo, 'siteOrigin'>

// Social crawlers (LinkedIn, X, Slack, Jira) never run JavaScript, so the
// branding the client normally fetches from /theme has to already be present in
// the server-rendered HTML. The same host always resolves to the same tenant and
// this data is public, so one short-lived cache entry per host keeps the extra
// round trip off the critical path for subsequent requests.
const CACHE_TTL_MS = 5 * 60 * 1000

const cache = new Map<string, { expires: number; value: CachedTenantSeo }>()

export const getTenantSeo = async (req?: any): Promise<TenantSeo | null> => {
  const host = req?.headers?.host
  if (!host) return null

  // Each proxy in the chain appends to x-forwarded-proto, so behind more than
  // one hop this arrives as "https, http" — the first entry is the scheme the
  // client actually used. Derived on every request and deliberately kept out of
  // the cache: one internal request without the proxy header would otherwise
  // pin http:// into og:url for every visitor until the entry expires.
  const rawProto = req?.headers?.['x-forwarded-proto']
  const protocol =
    (Array.isArray(rawProto) ? rawProto[0] : rawProto)?.split(',')[0]?.trim() || 'http'
  const siteOrigin = `${protocol}://${host}`

  const cached = cache.get(host)
  if (cached && cached.expires > Date.now()) return { ...cached.value, siteOrigin }

  // Host is client-supplied, so a miss can be for a host that never comes back.
  // Sweeping the expired entries here keeps the map from growing without bound
  // in a long-lived server process.
  for (const [key, entry] of cache) {
    if (entry.expires <= Date.now()) cache.delete(key)
  }

  try {
    // Backend resolves a public custom-domain tenant from Origin/Referer, which
    // the browser sends but server-side requests do not — so forward them.
    // No user id is passed: only the public branding is needed here, which keeps
    // the response identical for every visitor and therefore safe to cache.
    // axios is used directly rather than CommonApi: CommonApi is a browser-side
    // client (cookies, sessionStorage) and its error handler sets the global
    // "domain inactive" flag, which on the server is shared by every tenant.
    const resp = await axios({
      method: 'get',
      url: `${process.env.NEXT_PUBLIC_BASE_URL}theme`,
      headers: { Origin: siteOrigin, Referer: `${siteOrigin}/` }
    })

    const themeData = resp?.data?.data
    if (!themeData) return null

    // default_logo is intentionally not a fallback here: it holds "light"/"dark",
    // not an image URL.
    const value: CachedTenantSeo = {
      businessName: themeData.business_name || '',
      businessCode: themeData.code || '',
      ogImage: themeData.thumbnails?.[0]?.url || themeData.logo || '',
      favicon: themeData.logo_mark || '',
      hideFromGoogle: themeData.hide_from_google ?? false
    }

    cache.set(host, { expires: Date.now() + CACHE_TTL_MS, value })
    return { ...value, siteOrigin }
  } catch (err) {
    return null
  }
}
